import { useMemo } from 'react';
import { useMasters } from '../data/queries.js';
import { useAuth } from './AuthProvider.js';
import { useSharedTabletOperator } from './SharedTabletOperatorProvider.js';
import { useAccountAccessState } from '../data/queries.js';

export interface OperationalPersonnelRow {
  id: string;
  tenant_id: string;
  business_id?: string | null;
  full_name: string;
  role: string;
  staff_profile_id?: string | null;
  attivo?: boolean | null;
  archived_at?: string | null;
}

interface StaffProfileLink {
  id: string;
  tenant_id: string;
  user_id: string;
  archived_at?: string | null;
}

export function useOperationalIdentity(): {
  personnelId: string | null;
  personnelName: string | null;
  subjects: string[];
  canManageOperationalWork: boolean;
  personnel: OperationalPersonnelRow[];
} {
  const { profile } = useAuth();
  const accountAccess = useAccountAccessState();
  const tablet = useSharedTabletOperator();
  const { data: personnelRows = [] } = useMasters<OperationalPersonnelRow>('tenant_personnel');
  const { data: staffProfiles = [] } = useMasters<StaffProfileLink>('staff_profiles');

  return useMemo(() => {
    const personnel = personnelRows.filter((person) => (
      person.attivo !== false && !person.archived_at &&
      (!person.business_id || person.business_id === profile?.active_business_id)
    ));
    let person = tablet.isSharedTablet && tablet.activeOperator
      ? personnel.find((row) => row.id === tablet.activeOperator?.tenantPersonnelId) ?? null
      : null;
    if (!tablet.isSharedTablet && profile?.id) {
      const staff = staffProfiles.find((row) => row.user_id === profile.id && !row.archived_at);
      person = staff ? personnel.find((row) => row.staff_profile_id === staff.id) ?? null : null;
    }
    const role = profile?.role ?? 'staff';
    const fallback = tablet.isSharedTablet
      ? [accountAccess.visibilitySubject, 'staff']
      : [accountAccess.visibilitySubject || role, role];
    const subjects = person
      ? [`person:${person.id}`, ...fallback]
      : fallback;
    return {
      personnelId: person?.id ?? null,
      personnelName: person?.full_name ?? tablet.activeOperator?.fullName ?? profile?.full_name ?? null,
      subjects: [...new Set(subjects)],
      canManageOperationalWork: profile?.is_platform_owner === true ||
        ['consulente', 'ristoratore', 'osa'].includes(role),
      personnel,
    };
  }, [accountAccess.visibilitySubject, personnelRows, profile, staffProfiles, tablet.activeOperator, tablet.isSharedTablet]);
}
